/* Confirmação de PUBLICAÇÃO de uma versão legal (ADR-0774).

   Por que um Dialog destrutivo e não um clique com toast: publicar termos não é editar conteúdo, é
   assumir obrigação contratual perante todos os usuários — e é irreversível pelo desenho (não existe
   "editar de volta", só publicar a versão N+1). Dois passos deliberados, e nada de dismissal no meio:
   `locked` enquanto o publish está em flight (ADR-0216), senão um ESC deixa o operador sem saber se
   o texto entrou no ar.

   `effectiveAt` é oferecido aqui porque é SEPARADO de `publishedAt`: publicar hoje com vigência em 30
   dias é o caso normal de compliance (aviso prévio de mudança de termos). Sem este campo na UI o
   recurso existe no banco e não existe na prática. */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useLegalAdminContent } from '../../content/sections/legal-admin.js';
import type { LegalVersionGroup } from '../../lib/legal-versions.js';

type Timing = 'now' | 'scheduled';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly group: LegalVersionGroup;
  readonly docLabel: string;
  readonly pending: boolean;
  /** Mensagem já mapeada do erro da mutation — fica no dialog (é longa e acionável). */
  readonly error: string | null;
  /** `undefined` = vigorar agora (o servidor crava o instante). */
  readonly onConfirm: (effectiveAt: Date | undefined) => void;
}

export function LegalPublishDialog({
  open,
  onClose,
  group,
  docLabel,
  pending,
  error,
  onConfirm,
}: Props) {
  const [timing, setTiming] = useState<Timing>('now');
  const [effectiveAt, setEffectiveAt] = useState('');
  const copy = useLegalAdminContent().publishDialog;

  /* Reset ao reabrir — uma data digitada e abandonada não pode vazar para a próxima publicação. */
  useEffect(() => {
    if (open) {
      setTiming('now');
      setEffectiveAt('');
    }
  }, [open]);

  const scheduledDate = timing === 'scheduled' ? new Date(effectiveAt) : null;
  const scheduledValid = scheduledDate !== null && !Number.isNaN(scheduledDate.getTime());
  const canConfirm = !pending && (timing === 'now' || scheduledValid);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !pending && onClose()}>
      <DialogContent locked={pending} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-destructive">
            {copy.title(group.version, docLabel)}
          </DialogTitle>
          <DialogDescription>{copy.description(group.version)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          <InlineAlert variant="warning">
            {copy.localesWarning(group.locales.map((row) => row.locale).join(', '))}
          </InlineAlert>
          <RadioGroup
            value={timing}
            onValueChange={(value) => setTiming(value === 'scheduled' ? 'scheduled' : 'now')}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="now" id="legal-timing-now" />
              <Label htmlFor="legal-timing-now">{copy.timingNow}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="scheduled" id="legal-timing-scheduled" />
              <Label htmlFor="legal-timing-scheduled">{copy.timingScheduled}</Label>
            </div>
          </RadioGroup>
          {timing === 'scheduled' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal-effective-at">{copy.effectiveAtLabel}</Label>
              <Input
                id="legal-effective-at"
                type="datetime-local"
                value={effectiveAt}
                onChange={(e) => setEffectiveAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{copy.scheduledHint}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {copy.cancel}
          </Button>
          <Button
            variant="destructive"
            loading={pending}
            disabled={!canConfirm}
            onClick={() =>
              onConfirm(scheduledValid && scheduledDate !== null ? scheduledDate : undefined)
            }
          >
            {copy.confirm(group.version)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
